import { UserPlus, Users } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { type Member, listMembers } from "@/features/team/api";
import { AddMemberDialog } from "@/features/team/components/team-tab";
import { HubStepShell } from "../hub/hub-step-shell";
import type { OnboardingStepProps } from "./types";

/** Step 6 — invite teammates, reusing the Team page's AddMemberDialog +
 * createMember flow. "Continue" advances. */
export function InviteTeamStep({ meta, index, isFirst, onBack, onSkip, onSaved }: OnboardingStepProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    listMembers()
      .then(setMembers)
      .catch(() => toast.error("Couldn't load your team."));
  }, []);

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSaved();
      }}
    >
      <HubStepShell
        meta={meta}
        index={index}
        onBack={onBack}
        onSkip={onSkip}
        isFirst={isFirst}
        submitting={false}
        continueLabel="Continue"
      >
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground inline-flex items-center gap-2 text-xs font-semibold tracking-[0.08em] uppercase">
            <Users className="size-3.5" /> Your team ({members.length})
          </span>
          <ul className="divide-border divide-y overflow-hidden rounded-md border">
            {members.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate font-medium">{m.name}</span>
                  <span className="text-muted-foreground truncate text-xs">{m.email}</span>
                </span>
                <span className="text-muted-foreground shrink-0 text-xs capitalize">
                  {(m.role ?? "").replace(/_/g, " ")}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Button type="button" variant="outline" onClick={() => setAddOpen(true)}>
            <UserPlus />
            Add a teammate
          </Button>
        </div>
      </HubStepShell>

      <AddMemberDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={(m) => setMembers((prev) => [...prev, m])}
      />
    </form>
  );
}
